import type { ITenantThemeConfig } from '@/shared/api/types/tenant-types'
import { useParams } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTenantByIdQuery } from '@/entities/tenant'
import {
  TenantFeatureAvailability,
  TenantGeneralInfo,
  TenantLanguageConfig,
  TenantSettingsManagement,
  TenantThemeConfig,
  TenantThemePreview,
} from '@/features/tenant'
import { TenantDestinationManager } from '@/features/tenant-destination'
import { TENANT_THEME_DEFAULT_COLORS } from '@/shared/constants'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { useUnsavedChangesGuard } from '@/shared/hooks/use-unsaved-changes-guard'
import { Button } from '@/shared/ui/button'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Skeleton } from '@/shared/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'

const DEFAULT_THEME: ITenantThemeConfig = { primaryColor: TENANT_THEME_DEFAULT_COLORS.primary }

interface TenantDetailPageProps {
  tenantId?: string | null
}

export function TenantDetailPage({ tenantId }: TenantDetailPageProps = {}) {
  const { t } = useTranslation()
  const scopedAdminPath = useScopedAdminPath()
  const { id } = useParams({ strict: false })
  const resolvedTenantId = tenantId ?? id ?? ''
  const { data: response, isLoading } = useTenantByIdQuery(resolvedTenantId, { withSettings: true })

  const tenant = response?.data
  const [previewTheme, setPreviewTheme] = useState<ITenantThemeConfig>(DEFAULT_THEME)
  const [themeSaveCtx, setThemeSaveCtx] = useState<{
    onSave: () => void
    disabled: boolean
    isPending: boolean
  } | null>(null)
  const handleThemeSaveReady = useCallback(
    (ctx: { onSave: () => void; disabled: boolean; isPending: boolean }) => {
      setThemeSaveCtx(ctx)
    },
    [],
  )
  const [dirtySections, setDirtySections] = useState({
    general: false,
    theme: false,
    languages: false,
    features: false,
    settings: false,
  })

  const updateDirtySection = useCallback(
    (key: 'general' | 'theme' | 'languages' | 'features' | 'settings', isDirty: boolean) => {
      setDirtySections((prev) => {
        if (prev[key] === isDirty) {
          return prev
        }
        return { ...prev, [key]: isDirty }
      })
    },
    [],
  )
  const handleGeneralDirtyChange = useCallback(
    (isDirty: boolean) => updateDirtySection('general', isDirty),
    [updateDirtySection],
  )
  const handleThemeDirtyChange = useCallback(
    (isDirty: boolean) => updateDirtySection('theme', isDirty),
    [updateDirtySection],
  )
  const handleLanguagesDirtyChange = useCallback(
    (isDirty: boolean) => updateDirtySection('languages', isDirty),
    [updateDirtySection],
  )
  const handleFeaturesDirtyChange = useCallback(
    (isDirty: boolean) => updateDirtySection('features', isDirty),
    [updateDirtySection],
  )
  const handleSettingsDirtyChange = useCallback(
    (isDirty: boolean) => updateDirtySection('settings', isDirty),
    [updateDirtySection],
  )
  const resetDirtySections = useCallback(() => {
    queueMicrotask(() => {
      setDirtySections((prev) => {
        if (!Object.values(prev).some(Boolean)) {
          return prev
        }
        return {
          general: false,
          theme: false,
          languages: false,
          features: false,
          settings: false,
        }
      })
    })
  }, [])

  const hasUnsavedChanges = Object.values(dirtySections).some(Boolean)
  const { dialog: unsavedChangesDialog } = useUnsavedChangesGuard(hasUnsavedChanges)

  useEffect(() => {
    if (tenant?.themeConfig) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- seed editable preview state from async-loaded tenant data
      setPreviewTheme(tenant.themeConfig)
    }
  }, [tenant?.themeConfig])

  useEffect(() => {
    resetDirtySections()
  }, [resetDirtySections, tenant?.id])

  if (isLoading) {
    return (
      <PageContainer>
        <div className="space-y-4 mt-8">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </PageContainer>
    )
  }

  if (!tenant) {
    return (
      <PageContainer>
        <span className="text-sm text-zinc-500">{t('tenant.notFound')}</span>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader
        title={tenant.name}
        subtitle={t('tenant.detail.subtitle', {
          defaultValue: 'Manage tenant profile, configuration, and runtime behavior',
        })}
        breadcrumbs={[
          { label: t('sidebar.tenants'), path: scopedAdminPath('/tenant') },
          { label: tenant.name },
        ]}
      />

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList className="h-auto rounded-lg bg-zinc-100 p-1">
          <TabsTrigger value="general" className="px-4 py-2">
            {t('tenant.tabs.general')}
          </TabsTrigger>
          <TabsTrigger value="config" className="px-4 py-2">
            {t('tenant.tabs.config', { defaultValue: 'Tenant Config' })}
          </TabsTrigger>
          <TabsTrigger value="destinations" className="px-4 py-2">
            {t('tenantDestination.title', { defaultValue: 'Tenant destinations' })}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">
          <TenantGeneralInfo tenant={tenant} onDirtyChange={handleGeneralDirtyChange} />
        </TabsContent>

        <TabsContent value="config" className="space-y-6">
          <section className="border border-zinc-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200">
              <h2 className="text-sm font-semibold text-zinc-900">{t('tenant.tabs.theme')}</h2>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
              <TenantThemeConfig
                tenantId={tenant.id}
                onThemeChange={setPreviewTheme}
                onDirtyChange={handleThemeDirtyChange}
                {...(tenant.themeConfig ? { initialTheme: tenant.themeConfig } : {})}
                onSaveReady={handleThemeSaveReady}
              />
              <div className="flex flex-col gap-4">
                <TenantThemePreview theme={previewTheme} />
                <div className="mt-auto flex justify-end">
                  <Button
                    disabled={themeSaveCtx?.disabled ?? true}
                    onClick={() => themeSaveCtx?.onSave()}
                  >
                    {t('common.save')}
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <section className="border border-zinc-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-zinc-900">{t('tenant.tabs.languages')}</h2>
              {tenant.defaultLanguage && (
                <span className="text-xs text-zinc-500">
                  {t('tenant.language.default')}:{' '}
                  {tenant.defaultLanguage.nativeName || tenant.defaultLanguage.name} (
                  {tenant.defaultLanguage.code})
                </span>
              )}
            </div>
            <div className="p-4">
              <TenantLanguageConfig
                key={tenant.id}
                tenantId={tenant.id}
                onDirtyChange={handleLanguagesDirtyChange}
                {...(tenant.supportedLanguages
                  ? { initialLanguages: tenant.supportedLanguages }
                  : {})}
                {...(tenant.defaultLanguage?.code
                  ? { initialDefault: tenant.defaultLanguage.code }
                  : {})}
              />
            </div>
          </section>

          <section className="border border-zinc-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200">
              <h2 className="text-sm font-semibold text-zinc-900">
                {t('tenant.tabs.features', { defaultValue: 'Feature Availability' })}
              </h2>
            </div>
            <div className="p-4">
              <TenantFeatureAvailability
                key={tenant.id}
                tenantId={tenant.id}
                onDirtyChange={handleFeaturesDirtyChange}
              />
            </div>
          </section>

          <TenantSettingsManagement
            tenantId={tenant.id}
            onDirtyChange={handleSettingsDirtyChange}
          />
        </TabsContent>

        <TabsContent value="destinations" className="space-y-6">
          <TenantDestinationManager tenantId={tenant.id} />
        </TabsContent>
      </Tabs>
      {unsavedChangesDialog}
    </PageContainer>
  )
}
